import { appType } from "./appType";

// web(일반 브라우저)은 네이티브 스플래시가 없다 → 모듈 로드 시점에 이미 노출됨으로 확정한다.
// (렌더 전 동기 확정이라 useCountUpRoll 첫 렌더가 0 으로 시작해 target→0 깜빡임이 없다.)
let revealed = appType() === "Web";
const listeners = new Set<() => void>();

/** 스플래시가 노출되기 시작함(콜백 또는 web) → 이후 카운트업이 0→target 롤업. 단조·1회. */
export const markSplashRevealed = (): void => {
  if (revealed) return;
  revealed = true;
  listeners.forEach((listener) => listener());
};

/**
 * window.onSplashReveal 등록. App 루트에서 1회 호출한다.
 *  - native(iOS/Android): 콜백을 window 에 등록 — 네이티브가 스플래시 페이드 아웃을 시작하는 지점
 *    (콘텐츠가 보이기 시작)에서 호출하면 markSplashRevealed 로 롤업을 시작한다. 콜백이 영영 안 와도
 *    (구버전 앱) revealed 는 false 로 남고, useCountUpRoll 기본값이 target 이라 현행 동작(안전 degrade).
 *  - web: 모듈 초기에 이미 revealed=true → 등록 불필요(no-op).
 * 반드시 window 직접 등록 — ES module 지역 함수는 native 가 못 찾는다(silent drop).
 * cleanup 은 우리가 등록한 핸들러일 때만 제거(타 코드/재등록과의 삭제 경합 방지).
 */
export const bootstrapSplashReveal = (): (() => void) => {
  if (appType() === "Web") return () => {};
  const handler = () => markSplashRevealed();
  window.onSplashReveal = handler;
  return () => {
    if (window.onSplashReveal === handler) delete window.onSplashReveal;
  };
};

/** 현재 노출됐는지(스플래시가 걷히기 시작했는지) 스냅샷. */
export const isSplashRevealed = (): boolean => revealed;

/** 노출 상태 변화 구독(useSyncExternalStore 용). 반환값으로 해지. */
export const subscribeSplashRevealed = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

// ── 스플래시 완전 제거(fade-end) 신호 ────────────────────────────────
// reveal(fade-start)과 별개의 단조 신호다. reveal 은 "스플래시가 걷히기 시작"(아직 불투명),
// hidden 은 "스플래시가 완전히 제거됨"(alpha=0, 콘텐츠가 온전히 보임)을 뜻한다. 프로모션 카드 진입
// grow 는 이 hidden 시점에 시작해야 스플래시 뒤로 흘러가지 않는다(#325). 카운트업(reveal)과 다른 신호이므로
// reveal 함수군은 건드리지 않고 대칭으로 병렬 추가한다.
// web(스플래시 없음)은 모듈 초기에 이미 hidden=true.
let hidden = appType() === "Web";
const hiddenListeners = new Set<() => void>();

/** 스플래시가 완전히 제거됨(콜백 또는 web) → 프로모션 진입 grow 시작. 단조·1회. */
export const markSplashHidden = (): void => {
  if (hidden) return;
  hidden = true;
  hiddenListeners.forEach((listener) => listener());
};

/**
 * window.onSplashHidden 등록. App 루트에서 1회 호출한다(bootstrapSplashReveal 과 대칭).
 *  - native(iOS/Android): 네이티브가 스플래시 페이드 아웃을 **완료(오버레이 제거)** 하는 지점에서 호출 →
 *    markSplashHidden 으로 프로모션 진입 grow 를 시작한다. 콜백이 영영 안 와도(구버전) hidden 은 false 로
 *    남고, 프로모션은 idle 로 카드가 그대로 보인다(안전 degrade). reveal 은 있는데 hidden 만 없는 중간 버전은
 *    usePromotionQueue 의 fallback 이 처리한다.
 *  - web: 모듈 초기에 이미 hidden=true → 등록 불필요(no-op).
 * 반드시 window 직접 등록 — ES module 지역 함수는 native 가 못 찾는다(silent drop).
 */
export const bootstrapSplashHidden = (): (() => void) => {
  if (appType() === "Web") return () => {};
  const handler = () => markSplashHidden();
  window.onSplashHidden = handler;
  return () => {
    if (window.onSplashHidden === handler) delete window.onSplashHidden;
  };
};

/** 스플래시가 완전히 제거됐는지 스냅샷. */
export const isSplashHidden = (): boolean => hidden;

/** 완전 제거 상태 변화 구독(useSyncExternalStore 용). 반환값으로 해지. */
export const subscribeSplashHidden = (listener: () => void): (() => void) => {
  hiddenListeners.add(listener);
  return () => {
    hiddenListeners.delete(listener);
  };
};
